import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
 
        useCreatePlantillaSemanalMutation, 
        useUpdatePlantillaSemanalMutation, 
        useDeletePlantillaSemanalMutation 
    } from '../../services/hooks';
import { fetchNiveles, fetchInstructores, fetchPlantillasSemanales } from '../../services/api';
import { PlantillaSemanal, Nivel, Instructor } from '../../types/index';
import { toast } from 'react-toastify';
import { X, PlusCircle, Edit, Trash2, Eye } from 'lucide-react';
import { getEventBackgroundColor } from '../../utils/utils';
import { refId, refNombre } from '../../utils/plantillaRefs';
import { useBranding } from '../../contexts/BrandingContext';

interface PlantillasSemanalesProps {
    onClose: () => void;
}

interface NewEvento {
    nivel_id: string;
    instructor_id: string;
    dia: string;
    sala: string;
    horaInicio: string;
    horaFin: string;
}

interface CrearEventosMasivosParams {
    plantillaIds: string[];  // Array de IDs de plantillas
    fechaInicio: Date;
    fechaFin: Date;
}

const PlantillasSemanales: React.FC<PlantillasSemanalesProps> = ({ onClose }) => {
  const { branding } = useBranding();
  const roomOptions = branding?.rooms ?? [];

  // Query para obtener plantillas
    const { data: plantillas, isLoading, isError } = useQuery<PlantillaSemanal[]>({
        queryKey: ['plantillasSemanales'],
        queryFn: fetchPlantillasSemanales,
    });

    // Queries para llenar selects (niveles e instructores)
    const { data: niveles } = useQuery({
        queryKey: ['niveles'],
        queryFn: fetchNiveles,
    });

    const { data: instructores } = useQuery({
        queryKey: ['instructores'],
        queryFn: fetchInstructores,
    });

    // Estado para controlar la expansión de cada plantilla
    const [expandedId, setExpandedId] = useState<string | null>(null);


    const toggleExpand = (id: string) => {
        setExpandedId(prevId => (prevId === id ? null : id));
    };

    // Mutaciones para operaciones CRUD
    const createMutation = useCreatePlantillaSemanalMutation();
    const updateMutation = useUpdatePlantillaSemanalMutation();
    const deleteMutation = useDeletePlantillaSemanalMutation();


    // Estado y manejo básico para el formulario de creación
    const [newNumeroPlantilla, setNewNumeroPlantilla] = useState<number>(0);

    // Estado para la descripción de la plantilla
    const [newDescripcionPlantilla, setNewDescripcionPlantilla] = useState<string>('');
    
    // Estado para los nuevos eventos (plural)
    const [newEventos, setNewEventos] = useState<NewEvento[]>([]);

    // Estado para el id de la plantilla que se está editando
    const [editingPlantillaId, setEditingPlantillaId] = useState<string>('');

    // Estado para las plantillas seleccionadas
    const [selectedPlantillas, setSelectedPlantillas] = useState(new Set());


    // Función para agregar una nueva fila de evento
    const addNewEventoRow = () => {
        setNewEventos([
        ...newEventos,
        { nivel_id: '', instructor_id: '', dia: '', sala: '', horaInicio: '', horaFin: '' }
        ]);
    };


    const odernarNewEventos = () => {
        const diaOrder: Record<string, number> = {
            "Lunes": 1,
            "Martes": 2,
            "Miércoles": 3,
            "Jueves": 4,
            "Viernes": 5,
            "Sábado": 6,
            "Domingo": 7,
        };

        const newEventosOrdenados = newEventos.sort((a, b) => {
            if (diaOrder[a.dia] < diaOrder[b.dia]) return -1;
            if (diaOrder[a.dia] > diaOrder[b.dia]) return 1;
            if (a.horaInicio < b.horaInicio) return -1;
            if (a.horaInicio > b.horaInicio) return 1;
            return 0;
        });

        return newEventosOrdenados;
    };

    //Funcion para validar los campos de los eventos
    const validateEventos = () => {
        // Validación: verificar que se haya agregado al menos un evento
        if (newEventos.length === 0) {
            toast.error('Agrega al menos un evento.');
            return false;
        }

        // Validar cada evento
        for (const evento of newEventos) {
            if (
                !evento.nivel_id ||
                !evento.instructor_id ||
                !evento.dia ||
                !evento.sala ||
                !evento.horaInicio ||
                !evento.horaFin
            ) {
                toast.error('Por favor complete todos los campos de cada evento.');
                return false;
            }
            if (evento.horaInicio >= evento.horaFin) {
                toast.error('La hora de inicio debe ser menor que la hora de fin en cada evento.');
                return false;
            }
        }

        if (newNumeroPlantilla === 0) {
            toast.error('La semana no puede ser 0.');
            return false;
        }
        return true;
    };

    // Función para actualizar un campo de un evento
    const updateNewEvento = (index: number, field: keyof NewEvento, value: string) => {
        const updatedEventos = [...newEventos];
        updatedEventos[index] = { ...updatedEventos[index], [field]: value };
        setNewEventos(updatedEventos);
        // console.log('updatedEventos', updatedEventos);
    };


    // Función para crear una nueva plantilla
    const handleCreate = () => {
        if (!validateEventos()) return;

        const eventosOrdenados = odernarNewEventos();
        

        const newPlantilla: PlantillaSemanal = { 
            numero_plantilla: newNumeroPlantilla,
            descripcion: newDescripcionPlantilla || undefined,
            eventos: eventosOrdenados
        };

        createMutation.mutate(newPlantilla);
        console.log('newEventos', eventosOrdenados);
        resetFields();
    };

    // Función para actualizar una plantilla
    const handleUpdate = () => {
        if (!validateEventos()) return;


        const eventosOrdenados = odernarNewEventos();

        const updatedPlantilla: PlantillaSemanal = {
            _id: editingPlantillaId,
            numero_plantilla: newNumeroPlantilla,
            descripcion: newDescripcionPlantilla || undefined,
            eventos: eventosOrdenados
        };

        updateMutation.mutate(updatedPlantilla);
        resetFields();
        
    };


    //Funcion para actualizar los eventos de una plantilla
    const handleUpdatePlantilla = (plantilla_id: string) => {
        const selectedPlantilla = plantillas!.find(plantilla => plantilla._id === plantilla_id);
        if(!selectedPlantilla) return;
        setEditingPlantillaId(selectedPlantilla._id!);
        setNewNumeroPlantilla(selectedPlantilla.numero_plantilla);
        setNewDescripcionPlantilla(selectedPlantilla.descripcion || '');
        setNewEventos(selectedPlantilla.eventos.map(evento => ({
            nivel_id: refId(evento.nivel_id),
            instructor_id: refId(evento.instructor_id),
            dia: evento.dia,
            sala: evento.sala,
            horaInicio: evento.horaInicio,
            horaFin: evento.horaFin
        })));
    };


    //Función para determinar si se crea o modifica una plantilla
    const handleCreateUpdate = () => {
        if (editingPlantillaId !== '') {
            handleUpdate();
        } else {
            handleCreate();
        }
    };


    // console.log(plantillas);


     //Función para resetear los campos del formulario
    const resetFields = () => {
        setEditingPlantillaId('');
        setNewNumeroPlantilla(0);
        setNewEventos([]);
        setNewDescripcionPlantilla('');
    }

    if (isLoading) return <div>Cargando plantillas...</div>;
    if (isError) return <div>Error al cargar plantillas.</div>;

    return (
        <div className="modal-overlay fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="relative modal-content bg-white rounded-lg p-6 w-full max-w-4xl overflow-y-auto max-h-[80vh]">
                <button 
                    onClick={onClose} 
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                >
                    <X size={24} />
                </button>
                <div className="flex flex-col justify-between items-left mb-4">
                    <h2 className="text-2xl font-bold">Plantillas Semanales</h2>
                    <div className='p-2'>
                        <p>Puedes crear plantillas para posteriomente usarlas para crear eventos recurrentes.</p>
                        <p>Las plantillas estan diseñadas para guardar los eventos semanales y luego replicarlos en un rango de fechas.</p>
                    </div>
                </div>
                <div>
                    {plantillas?.map(plantilla => (
                        <div key={plantilla._id} className="border rounded p-2 mb-2">
                            <div className="flex justify-between items-center cursor-pointer" onClick={() => toggleExpand(plantilla._id!)}>
                                <div className='flex justify-start gap-4 items-center'>
                                    <h3><strong>Plantilla Nro:</strong> {plantilla.numero_plantilla}</h3>
                                    <span><strong>Descripcion:</strong> {plantilla.descripcion}</span>
                                </div>
                                <div className="flex items-center gap-4 ml-auto" >
                                    <Eye 
                                        className="cursor-pointer text-green-700"
                                        // onClick={() => toggleExpand(plantilla._id!)}
                                    />
                                    <Edit
                                        className="cursor-pointer text-blue-500"
                                        onClick={(e: React.MouseEvent<SVGElement, MouseEvent>) => {
                                            e.stopPropagation();
                                            handleUpdatePlantilla(plantilla._id!);
                                        }}
                                    />
                                    <Trash2
                                        className="cursor-pointer text-red-500"
                                        onClick={(e: React.MouseEvent<SVGElement, MouseEvent>) => {
                                            e.stopPropagation();
                                            deleteMutation.mutate(plantilla._id!);
                                        }}
                                    />
                                    {expandedId === plantilla._id! ? '▲' : '▼'}
                                </div>
                            </div>
                            {expandedId === plantilla._id && (
                                <div className="mt-2 p-2 flex justify-start gap-4 bg-blue-50">
                                    {Object.entries(
                                        plantilla.eventos.reduce<Record<string, typeof plantilla.eventos>>((acc, evento) => {
                                            const dia = evento.dia;
                                            if (!acc[dia]) {
                                            acc[dia] = [];
                                            }
                                            acc[dia].push(evento);
                                            return acc;
                                        }, {})
                                        ).map(([dia, eventos]) => (
                                        <div key={dia}>
                                            <div className='bg-slate-500 rounded-lg m-1 text-white font-bold text-center'>
                                                {dia}
                                            </div>
                                            
                                            <ul>
                                            {eventos.map((evento, index) => {
                                                const backgroundColor = typeof evento.nivel_id === 'object' && evento.nivel_id !== null && 'nombre' in evento.nivel_id
                                                    ? getEventBackgroundColor(evento.nivel_id as { nombre: string; color?: string })
                                                    : '#6b7280'; // gray-500
                                                
                                                return (
                                                <li 
                                                    key={index} 
                                                    className="text-xs text-white rounded-lg mb-1 border-b p-2"
                                                    style={{ backgroundColor }}
                                                >
                                                    <p>
                                                        <strong>Nivel:</strong> {refNombre(evento.nivel_id) ?? <em className="font-bold underline">eliminado, reasígnalo</em>}
                                                    </p>
                                                    <p>
                                                        <strong>Instructor:</strong> {refNombre(evento.instructor_id) ?? <em className="font-bold underline">eliminado, reasígnalo</em>}
                                                    </p>
                                                    <p>
                                                        <strong>Sala:</strong> {evento.sala}
                                                    </p>
                                                    <p>
                                                        <strong>Hora:</strong> {evento.horaInicio} - {evento.horaFin}
                                                    </p>
                                                </li>
                                                );
                                            })}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                <div className="mt-4 border-t pt-4">
                    <h3 className="font-bold mb-2">Crear Plantilla</h3>
                    <div className="p-2 flex flex-col gap-4 text-sm">
                        <div className="flex items-center gap-2">
                            <div className='flex justify-start gap-2 items-center'>
                                <label>
                                    <strong>Plantilla Nro:</strong> 
                                </label>
                                <input
                                    type="number"
                                    value={newNumeroPlantilla}
                                    onChange={e => setNewNumeroPlantilla(Number(e.target.value))}
                                    className="border rounded px-2 py-1 w-20"
                                />
                            </div>
                            <div className='flex justify-start gap-2 items-center'>
                                <label>
                                    <strong>Descripcion (opcional):</strong>
                                </label>
                                <input
                                    type="text"
                                    value={newDescripcionPlantilla}
                                    onChange={e => setNewDescripcionPlantilla(String(e.target.value))}
                                    className="border rounded px-2 py-1"
                                />
                            </div>
                            <button
                                className="px-2 py-2 bg-brand-primary hover:brightness-90 text-white rounded p-1 flex items-center gap-2"
                                onClick={addNewEventoRow}
                            >
                                Agregar Eventos <PlusCircle size={18} /> 
                            </button>
                        </div>
                        {newEventos.map((evento, index) => (
                            <div key={index} className="border rounded p-2 flex flex-wrap gap-2 items-center">
                                <div className='flex flex-col items-left'>
                                    <label>Nivel:</label>
                                    <select
                                        value={evento.nivel_id}
                                        onChange={e => updateNewEvento(index, 'nivel_id', e.target.value)}
                                        className="border rounded px-2 py-1"
                                    >
                                        <option value="">Seleccione</option>
                                        {niveles?.map((nivel: Nivel) => (
                                            <option key={nivel._id} value={nivel._id}>
                                                {nivel.nombre}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className='flex flex-col items-left'>
                                    <label>Instructor:</label>
                                    <select
                                        value={evento.instructor_id}
                                        onChange={e => updateNewEvento(index, 'instructor_id', e.target.value)}
                                        className="border rounded px-2 py-1"
                                    >
                                        <option value="">Seleccione</option>
                                        {instructores?.map((instructor: Instructor) => (
                                            <option key={instructor._id} value={instructor._id}>
                                                {instructor.nombre}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className='flex flex-col items-left'>
                                    <label>Día:</label>
                                    <select
                                        value={evento.dia}
                                        onChange={e => updateNewEvento(index, 'dia', e.target.value)}
                                        className="border rounded px-2 py-1"
                                    >
                                        <option value="">Seleccione</option>
                                        <option value="Lunes">Lunes</option>
                                        <option value="Martes">Martes</option>
                                        <option value="Miércoles">Miércoles</option>
                                        <option value="Jueves">Jueves</option>
                                        <option value="Viernes">Viernes</option>
                                        <option value="Sábado">Sábado</option>
                                        <option value="Domingo">Domingo</option>
                                    </select>
                                </div>
                                <div className='flex flex-col items-left'>
                                    <label>Sala:</label>
                                    <select
                                        value={evento.sala}
                                        onChange={e => updateNewEvento(index, 'sala', e.target.value)}
                                        className="border rounded px-2 py-1"
                                    >
                                        <option value="">Selecciona</option>
                                        {roomOptions.map((room) => (
                                            <option key={room.name} value={room.name}>
                                                {room.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className='flex flex-col items-left'>
                                    <label>Hora Inicio:</label>
                                    <input
                                        type="time"
                                        min="08:00"
                                        max="22:00"
                                        step="1800"
                                        value={evento.horaInicio}
                                        onChange={e => updateNewEvento(index, 'horaInicio', e.target.value)}
                                        className="border rounded px-2 py-1"
                                    />
                                </div>
                                <div className='flex flex-col items-left'>
                                    <label>Hora Fin:</label>
                                    <input
                                        type="time"
                                        min="08:00"
                                        max="22:00"
                                        step="1800"
                                        value={evento.horaFin}
                                        onChange={e => updateNewEvento(index, 'horaFin', e.target.value)}
                                        className="border rounded px-2 py-1"
                                    />
                                </div>
                                <div className='align-middle pl-4 flex items-center gap-4'>
                                    {index === newEventos.length - 1 && (<button
                                        className="px-2 py-2 bg-brand-primary hover:brightness-90 text-white rounded-full"
                                        onClick={addNewEventoRow}
                                    >
                                        <PlusCircle size={18} /> 
                                    </button>)}
                                    <button
                                        className="px-2 py-2 bg-red-500 text-white rounded-full"
                                        onClick={() => setNewEventos(newEventos.filter((_, i) => i !== index))}
                                    >
                                        <X size={18} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className='flex justify-end gap-4 mt-4'>
                            {newEventos.length > 0 && (
                                <button
                                    className="mt-2 px-4 py-2 bg-brand-primary hover:brightness-90 text-white rounded"
                                    onClick={handleCreateUpdate}
                                >
                                    {(editingPlantillaId !== '') ? 'Actualizar Plantilla' : 'Crear Plantilla'}
                                </button>
                            )}
                            {(editingPlantillaId !== '') && (
                                <button
                                    className="mt-2 px-4 py-2 bg-red-500 hover:brightness-90 text-white rounded"
                                    onClick={() => {
                                        resetFields()
                                    }}
                                >
                                    Cancelar
                                </button>
                            )}
                        <button className="mt-2 px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded" onClick={onClose}>
                            Cerrar
                        </button>
                    </div>  
                    </div>
                
            </div>
        </div>
    );
};

export default PlantillasSemanales;